/**
 * Generic calendar-grid and portaled-picker positioning math.
 *
 * Extracted from business/shared/ActionItemDueDatePicker.js (Phase 5a) --
 * these four functions have no action-item-specific logic, so they moved
 * here to let core/dataeng components (LookbackPeriodControl) use them
 * without reaching into a mantis-owned file. ActionItemDueDatePicker.js
 * re-exports them from here so its existing mantis-side importers are
 * unaffected.
 */

const PICKER_GAP_PX = 4;
const PICKER_MIN_WIDTH_PX = 220;
const PICKER_ESTIMATED_HEIGHT_PX = 300;
const PICKER_VIEWPORT_PADDING_PX = 8;

/**
 * @example
 * formatIsoDate(2026, 4, 26);
 * // "2026-05-26"
 */
export const formatIsoDate = (year, monthIndex, day) =>
  `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/**
 * @example
 * parseIsoDateParts("2026-05-26");
 * // { year: 2026, monthIndex: 4, day: 26 }
 */
export const parseIsoDateParts = (isoDate) => {
  const value = String(isoDate || "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return { year, monthIndex: month - 1, day };
};

/**
 * Build a 6×7 calendar grid for the visible month (includes adjacent-month padding days).
 *
 * @example
 * buildCalendarCells(2026, 4).filter((c) => c.inMonth).length;
 * // 31 (May 2026)
 */
export const buildCalendarCells = (viewYear, viewMonthIndex) => {
  const firstWeekday = new Date(viewYear, viewMonthIndex, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonthIndex + 1, 0).getDate();
  const cells = [];

  const prevMonthLastDay = new Date(viewYear, viewMonthIndex, 0).getDate();
  for (let i = firstWeekday - 1; i >= 0; i -= 1) {
    const day = prevMonthLastDay - i;
    const monthIndex = viewMonthIndex === 0 ? 11 : viewMonthIndex - 1;
    const year = viewMonthIndex === 0 ? viewYear - 1 : viewYear;
    cells.push({ year, monthIndex, day, inMonth: false });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ year: viewYear, monthIndex: viewMonthIndex, day, inMonth: true });
  }

  let nextDay = 1;
  while (cells.length < 42) {
    const monthIndex = viewMonthIndex === 11 ? 0 : viewMonthIndex + 1;
    const year = viewMonthIndex === 11 ? viewYear + 1 : viewYear;
    cells.push({ year, monthIndex, day: nextDay, inMonth: false });
    nextDay += 1;
  }

  return cells;
};

/**
 * Fixed viewport coordinates for a portaled due-date picker (flips above when needed).
 *
 * @example
 * computeDueDatePickerFixedStyle(anchorEl, pickerEl, { placement: "auto", align: "start" });
 * // { top: 412, left: 88, openAbove: true }
 */
export const computeDueDatePickerFixedStyle = (
  anchorEl,
  pickerEl,
  { placement = "auto", align = "start" } = {}
) => {
  if (!anchorEl) return null;

  const anchorRect = anchorEl.getBoundingClientRect();
  const pickerHeight = pickerEl?.offsetHeight || PICKER_ESTIMATED_HEIGHT_PX;
  const pickerWidth = Math.max(
    pickerEl?.offsetWidth || PICKER_MIN_WIDTH_PX,
    PICKER_MIN_WIDTH_PX
  );
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  const spaceBelow = viewportH - anchorRect.bottom - PICKER_GAP_PX;
  const spaceAbove = anchorRect.top - PICKER_GAP_PX;

  let openAbove = placement === "above";
  if (placement === "auto") {
    openAbove =
      spaceBelow < pickerHeight && spaceAbove >= spaceBelow
        ? true
        : spaceBelow < pickerHeight && spaceAbove < pickerHeight
          ? spaceAbove > spaceBelow
          : false;
  }

  let top = openAbove
    ? anchorRect.top - PICKER_GAP_PX - pickerHeight
    : anchorRect.bottom + PICKER_GAP_PX;
  top = Math.min(
    Math.max(PICKER_VIEWPORT_PADDING_PX, top),
    viewportH - pickerHeight - PICKER_VIEWPORT_PADDING_PX
  );

  let left =
    align === "end" ? anchorRect.right - pickerWidth : anchorRect.left;
  if (left + pickerWidth > viewportW - PICKER_VIEWPORT_PADDING_PX) {
    left = Math.max(
      PICKER_VIEWPORT_PADDING_PX,
      viewportW - pickerWidth - PICKER_VIEWPORT_PADDING_PX
    );
  }
  if (left < PICKER_VIEWPORT_PADDING_PX) {
    left = PICKER_VIEWPORT_PADDING_PX;
  }

  return { top, left, openAbove };
};
